import { ArrowUpRight } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { VIDEO, POSTER } from "@/lib/images";
import { BRANDS } from "@/data/company";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

export default function OurBrands() {
  return (
    <>
      <Hero
        image={POSTER.brands}
        video={VIDEO.brands}
        eyebrow="OUR BRANDS"
        title="The brands we build and back."
        subline="We don't just run AI departments for clients. We start and partner with companies of our own — and put the same operators and systems behind them."
        primary={{ label: "Install your department", to: "/audit" }}
        secondary={{ label: "Get in touch", href: "/contact" }}
      />

      {/* Brand grid */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="brands-grid">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal><Label>In our portfolio</Label></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                Companies we run the same way we run yours.
              </h2>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BRANDS.map((b, i) => (
              <Reveal key={b.name + i} delay={(i % 3) * 80}>
                <a
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/30 hover:shadow-[0_24px_50px_-28px_rgba(20,18,16,0.4)]"
                  data-testid={`brand-card-${i + 1}`}
                >
                  <div
                    className="flex h-16 w-16 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-lg font-bold tracking-tight text-[var(--ember)]"
                    aria-hidden="true"
                  >
                    {b.initials}
                  </div>
                  <h3 className="mt-6 text-xl font-semibold tracking-tight">
                    {b.name}
                  </h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[var(--muted)]">
                    {b.description}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ember)]">
                    Visit site
                    <ArrowUpRight
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partner band */}
      <section className="bg-[var(--dark)] text-[var(--dark-text)]" data-testid="brands-partner">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center md:px-12 md:py-28">
          <Reveal>
            <h2 className="t-section">
              Building something we should back?
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              We partner with founders and operators in our verticals.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-9">
              <Button to="/contact" variant="primary">
                Get in touch
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
